import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNotification } from '../utils/CustomNotification';
import { NODE_BACKEND_URL } from '../utils/constant';
import { getUser, getAuthToken } from '../utils/helpers';

/**
 * Hook to poll for video analysis status changes and show notifications
 * when analysis completes (success or failure).
 *
 * @param {Object} options Configuration options
 * @param {boolean} options.enabled Whether polling is enabled (default: true)
 * @param {number} options.pollInterval Polling interval in ms (default: 30000 - 30 seconds)
 * @param {string|null} options.folderId Optional folder ID to filter videos
 * @param {Function} options.onStatusChange Callback when any video status changes
 * @returns {Object} { processingVideos: number, checkNow: Function }
 */
const useVideoAnalysisNotifications = ({
  enabled = true,
  pollInterval = 30000,
  folderId = null,
  onStatusChange = null,
} = {}) => {
  const notification = useNotification();
  const previousVideosRef = useRef(new Map()); // Map of video id -> status
  const processingCountRef = useRef(0);
  const intervalRef = useRef(null);

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  /**
   * Show browser notification if permitted
   */
  const showBrowserNotification = useCallback((title, body, isError = false) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body,
          icon: isError ? '/favicon-error.ico' : '/favicon.ico',
          tag: 'video-analysis',
          requireInteraction: false,
        });
      } catch (error) {
        // Browser doesn't support notifications or they're blocked
        console.log('Browser notification not available:', error);
      }
    }
  }, []);

  /**
   * Show notification for completed analysis
   */
  const showCompletedNotification = useCallback((video) => {
    const videoName = video.video_name || video.title || 'Video';

    // In-app notification
    notification.success({
      message: 'Analysis Complete',
      description: `"${videoName}" has been analyzed successfully.`,
      duration: 6000,
    });

    // Browser notification (if permitted)
    showBrowserNotification(
      'Video Analysis Complete',
      `"${videoName}" has been analyzed successfully.`
    );
  }, [notification, showBrowserNotification]);

  /**
   * Show notification for failed analysis
   */
  const showFailedNotification = useCallback((video) => {
    const videoName = video.video_name || video.title || 'Video';
    const reason = video.error_message || 'Analysis failed. Please try again.';

    // In-app notification
    notification.error({
      message: 'Analysis Failed',
      description: `"${videoName}": ${reason}`,
      duration: 8000,
    });

    // Browser notification (if permitted)
    showBrowserNotification(
      'Video Analysis Failed',
      `"${videoName}": ${reason}`,
      true
    );
  }, [notification, showBrowserNotification]);

  /**
   * Fetch current video statuses and compare with previous
   */
  const checkVideoStatuses = useCallback(async () => {
    const user = getUser();
    if (!user?.id) return;

    try {
      const params = {
        user_id: user.id,
        limit: 50, // Check recent videos
        page: 1,
      };

      if (folderId) {
        params.folder_id = folderId;
      }

      const response = await axios.get(`${NODE_BACKEND_URL}/api/video-analysis`, {
        params,
        headers: { Authorization: getAuthToken() }
      });

      if (!response.data?.success) return;

      const videos = Array.isArray(response.data?.data) ? response.data.data : [];
      const previousVideos = previousVideosRef.current;
      let processingCount = 0;

      videos.forEach((video) => {
        const videoId = video.id;
        const currentStatus = video.status?.toLowerCase();
        const previousStatus = previousVideos.get(videoId);

        // Count processing videos
        if (currentStatus === 'processing' || currentStatus === 'uploaded') {
          processingCount++;
        }

        // Check for status transitions
        if (previousStatus && previousStatus !== currentStatus) {
          // Status changed from processing/uploaded to completed
          if (
            (previousStatus === 'processing' || previousStatus === 'uploaded') &&
            currentStatus === 'completed'
          ) {
            showCompletedNotification(video);
            if (onStatusChange) onStatusChange(video, 'completed');
          }
          // Status changed to failed
          else if (
            (previousStatus === 'processing' || previousStatus === 'uploaded') &&
            currentStatus === 'failed'
          ) {
            showFailedNotification(video);
            if (onStatusChange) onStatusChange(video, 'failed');
          }
        }

        // Update tracked status
        previousVideos.set(videoId, currentStatus);
      });

      processingCountRef.current = processingCount;
    } catch (error) {
      console.error('Error checking video statuses:', error);
    }
  }, [folderId, showCompletedNotification, showFailedNotification, onStatusChange]);

  /**
   * Manual check function that can be called externally
   */
  const checkNow = useCallback(() => {
    checkVideoStatuses();
  }, [checkVideoStatuses]);

  /**
   * Start polling for video status changes
   */
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Request notification permission on mount
    requestNotificationPermission();

    // Initial check
    checkVideoStatuses();

    // Set up polling interval
    intervalRef.current = setInterval(checkVideoStatuses, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, pollInterval, checkVideoStatuses, requestNotificationPermission]);

  return {
    processingVideos: processingCountRef.current,
    checkNow,
  };
};

export default useVideoAnalysisNotifications;
